<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';
import RadioButton from 'primevue/radiobutton';
import Menu, { type MenuPassThroughMethodOptions } from 'primevue/menu';
import {
  type MatrixColumn,
  MatrixColumnType,
  MATRIX_COLUMN_TYPES
} from '@/models';
import { ButtonType, CustomButton } from '@/components';

const props = defineProps({
  column: {
    type: Object as PropType<MatrixColumn>,
    required: true
  }
});
const localColumn = ref<MatrixColumn>(props.column);

const types = ref();
const handleTypeClick = (event: PointerEvent) => {
  types.value.toggle(event);
};
const currentSelectedType = computed(() =>
  MATRIX_COLUMN_TYPES.find(type => type.type === props.column.type)
);

const emits = defineEmits(['onDropdownChoicesClick', 'onColumnUpdate']);

const onDropdownChoicesClick = () => {
  emits('onDropdownChoicesClick', localColumn.value);
};

const onTypeSelect = (index: number) => {
  const type = MATRIX_COLUMN_TYPES[index].type;

  switch (type) {
    case MatrixColumnType.DATE:
      if (!localColumn.value.options) {
        localColumn.value.options = {
          mode: 'date'
        };
      } else {
        localColumn.value.options!.mode = 'date';
      }
      break;
      
    case MatrixColumnType.DROPDOWN:
      if (!localColumn.value.options) {
        localColumn.value.options = {
          choices: [
            {
              id: 1,
              label: 'Example choice',
              value: 1
            }
          ],
          multipleChoice: false
        };
      } else {
        localColumn.value.options.choices = [
          {
            id: 1,
            label: 'Example choice',
            value: 1
          }
        ];
        localColumn.value.options.multipleChoice = false;
      }
      break;
  
    default:
      break;
  }
  localColumn.value.type = type;
  emits('onColumnUpdate', localColumn.value);
};
</script>

<template>
  <div class="config-root">
    <div class="column-label">{{ column.label }}</div>
    <div class="column-type">
      <div class="type-label title">Type</div>
      <div class="type-select">
        <CustomButton :text="currentSelectedType?.label"
          :icon="{ style: currentSelectedType!.iconStyle, icon: currentSelectedType!.icon }"
          :font-weight="700"
          @onClick="handleTypeClick"
          class="field-type-button"
          :type="ButtonType.WITHOUT_OUTLINE"/>
        <Menu :model="MATRIX_COLUMN_TYPES"
          :popup="true"
          ref="types"
          :pt="{
            itemLink: (options: MenuPassThroughMethodOptions) =>({
                class: {
                    'selected-menu': options.context.index === MATRIX_COLUMN_TYPES.findIndex(type => type.type === column.type)
                },
                onClick: () => onTypeSelect(options.context.index)
            })
          }">
          <template #item="{ item, props }">
            <a v-ripple class="flex items-center" v-bind="props.action">
              <FontAwesomeIcon :icon="[`${item.iconStyle}`, `${item.icon}`]"
                class="icon"/>
              <div class="type-label">{{ item.label }}</div>
            </a>
          </template>
        </Menu>
      </div>
    </div>
    <div class="date-type-config"
      v-if="currentSelectedType?.type === MatrixColumnType.DATE">
      <div class="date-type-label title">Date type</div>
      <div class="date-type-otions">
        <div class="date">
          <RadioButton v-model="column.options!.mode" :value="'date'" />
          <label class="ml-2">Date</label>
        </div>
        <div class="date">
          <RadioButton v-model="column.options!.mode" :value="'time'" />
          <label class="ml-2">Time</label>
        </div>
        <div class="date">
          <RadioButton v-model="column.options!.mode" :value="'datetime'" />
          <label class="ml-2">Date & time</label>
        </div>
      </div>
      <div class="date-type-otions" v-if="column.options!.mode === 'time' || column.options!.mode === 'datetime'">
        <div class="date">
          <RadioButton v-model="column.options!.timeFormat" :value="12" />
          <label class="ml-2">12-hour</label>
        </div>
        <div class="date">
          <RadioButton v-model="column.options!.timeFormat" :value="24" />
          <label class="ml-2">24-hour</label>
        </div>
      </div>
    </div>
    <div class="dropdown-selection-types"
      v-if="currentSelectedType?.type === MatrixColumnType.DROPDOWN">
      <div class="title">Type of selection</div>
      <div class="selection-types">
        <div class="date">
          <RadioButton v-model="column.options!.multipleChoice"
            :value="false"/>
          <label class="ml-2">Single</label>
        </div>
        <div class="date">
          <RadioButton v-model="column.options!.multipleChoice"
            :value="true"/>
          <label class="ml-2">Multiple</label>
        </div>
      </div>
    </div>
    <div class="dropdown-choices"
      v-if="currentSelectedType?.type === MatrixColumnType.DROPDOWN">
      <div class="title">Choices</div>
      <div class="choices-list">
        <div class="choices-list-label title">
          {{ column.options!.choices?.length }} choices in list
        </div>
        <CustomButton text="Edit list"
          :icon="{ style: 'far', icon: 'pencil' }"
          class="edit-list-button"
          :marginLeft="16"
          @onClick="onDropdownChoicesClick"
          :type="ButtonType.PRIMARY_WITHOUT_OUTLINE"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.config-root {
  padding: 16px 0 16px 0;
  border-bottom: 1px solid #b8c2cc;
}
.column-label {
  font-weight: 600;
  margin-bottom: 16px;
}
.title {
  font-weight: 600;
}
.column-type {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.selected-menu {
  font-weight: 700;
  background-color: #e2e8f0;
  color: black;
  border-radius: 4px;
}
.date-type-config,
.dropdown-selection-types,
.dropdown-choices {
  margin-top: 20px;
}
.date-type-otions,
.selection-types,
.choices-list {
  display: flex;
  margin-top: 8px;
  justify-content: space-between;
  align-items: center;
}
.choices-list {
  margin: unset;
}
.date {
  display: flex;
  align-items: center;

  .ml-2 {
    margin-left: 8px;
  }
}
</style>
